import {useState} from 'react'
import sortBy from 'lodash/sortBy'
import {exhaustive} from '@freckle/exhaustive'
import {maybe} from '@freckle/maybe'
import type {NonEmptyArray} from '@freckle/non-empty'
import {mkNonEmpty, nonEmptyToArray} from '@freckle/non-empty'
import {useSafeEffectExtraDeps} from '@freckle/react-hooks'
import {staticExtra} from '@freckle/educator-entities/ts/common/helpers/extra-deps'
import {Dropdown} from '@freckle/educator-materials/ts/components/dropdown'
import type {TranslateT} from '@freckle/educator-materials/ts/helpers/translate'
import {useSafeTranslation} from '@freckle/educator-materials/ts/helpers/translate'
import type {OptionType} from '@freckle/educator-materials/ts/logic/dropdown'
import {flatOptions} from '@freckle/educator-materials/ts/logic/dropdown'
import type {
  DashboardIdentityProviderT,
  AllProvidersOrSpecificT,
} from '@freckle/school/ts/common/helpers/api-helper'
import {allIdentityProviders} from '@freckle/school/ts/common/helpers/api-helper'
import {getStudentCountForIdp, useStudentCounts} from '@freckle/school/ts/common/models/summary'
import {storeIdpsSelected, initialSelection} from './utils'
import {idpPickerDropdown} from './idp-picker.module.scss'

type Props = {
  currentIdps: AllProvidersOrSpecificT
  onChange: (a: AllProvidersOrSpecificT) => void
  adminId: number
  allSchoolIds: number[]
}

const toLabel =
  (t: TranslateT) =>
  (provider: DashboardIdentityProviderT): string => {
    switch (provider) {
      case 'manual':
        return t('IDP_PICKER_MENU_ITEM_MANUAL')
      case 'clever':
        return t('IDP_PICKER_MENU_ITEM_CLEVER')
      case 'class-link':
        return t('IDP_PICKER_MENU_ITEM_CLASSLINK')
      case 'csv':
        return t('IDP_PICKER_MENU_ITEM_CSV')
      case 'renaissance':
        return t('IDP_PICKER_MENU_ITEM_RENAISSANCE')
      default:
        return exhaustive(provider)
    }
  }

export function IdpPicker(props: Props) {
  const {currentIdps, onChange, adminId, allSchoolIds} = props
  const {t} = useSafeTranslation()
  const studentCounts = useStudentCounts(adminId, allSchoolIds).data

  const idpCountsLargerThanZero = allIdentityProviders().reduce((o, idp) => {
    if (studentCounts !== undefined) {
      const count = getStudentCountForIdp(studentCounts, idp)
      if (count > 0) o.set(idp, count)
    }
    return o
  }, new Map<DashboardIdentityProviderT, number>())

  const sortedIdps = sortBy(
    Array.from(idpCountsLargerThanZero.keys()),
    toLabel(t),
  ) as DashboardIdentityProviderT[]

  const [selection, setSelection] = useState<Array<DashboardIdentityProviderT>>(
    initialSelection(currentIdps, idpCountsLargerThanZero),
  )

  const selectAll = () => {
    setSelection(Array.from(idpCountsLargerThanZero.keys()) as DashboardIdentityProviderT[])
    onChange({tag: 'all'})
  }

  const selectSpecific = (specific: NonEmptyArray<DashboardIdentityProviderT>) => {
    setSelection(nonEmptyToArray(specific))
    onChange({tag: 'specific', specific})
  }

  const notify = (newSelection: DashboardIdentityProviderT[]) => {
    if (Array.from(idpCountsLargerThanZero.keys()).every(idp => newSelection.includes(idp))) {
      selectAll()
    } else {
      maybe(selectAll, selectSpecific, mkNonEmpty(newSelection))
    }
  }

  useSafeEffectExtraDeps<{
    notify: (newSelection: DashboardIdentityProviderT[]) => void
    selection: DashboardIdentityProviderT[]
  }>(({notify, selection}) => notify(selection), [], {
    notify: staticExtra(notify),
    selection: staticExtra(selection),
  })

  const idpItems = sortedIdps.map(idp => ({label: t('DISPLAY_IDP', {idp}), value: idp}))

  const getIdpOptions = (
    idpOptions: Array<OptionType<DashboardIdentityProviderT>>,
  ): Array<DashboardIdentityProviderT> => idpOptions.map(({value}) => value)

  const formatIdpOptions = (
    selection: Array<DashboardIdentityProviderT>,
  ): Array<OptionType<DashboardIdentityProviderT>> =>
    selection.map(idp => ({label: t('DISPLAY_IDP', {idp}), value: idp}))

  return (
    <div className={idpPickerDropdown}>
      <div data-pii id="idp-picker-dropdown">
        <Dropdown
          labelText={t('IDP_PICKER_LABEL')}
          options={flatOptions(idpItems)}
          isSearchable={false}
          isMultiSelect
          value={formatIdpOptions(selection)}
          handleChange={(idpOptions: ReadonlyArray<OptionType<DashboardIdentityProviderT>>) => {
            const mutableIdpOptions = [...idpOptions]
            const idps = getIdpOptions(mutableIdpOptions)
            notify(idps)
            storeIdpsSelected(idps)
          }}
          formatSelectionText={(numSelected: number) => {
            if (numSelected === 1) {
              return t('DISPLAY_IDP', {idp: selection[0]})
            }
            return t('DROPDOWN_MULTI_IDPS_SELECTED_LABEL', {
              numSelected,
            })
          }}
          allSelectedText={t('ALL_SOURCE_SELECTED_TEXT')}
          noneSelectedText={t('NO_SOURCE_SELECTED_TEXT')}
          isDisabled={Array.from(idpCountsLargerThanZero.keys()).length <= 1}
        />
      </div>
    </div>
  )
}
